import { useEffect } from 'react';

interface UseFocusFirstInteractiveElementProps {
  isOpen: boolean;
  currentStep?: number;
  delay?: number;
}

/**
 * Custom hook for managing focus within modal dialog steps
 * Focuses the first interactive element when the dialog opens or step changes
 * 
 * @param props Configuration options
 * @returns void
 */
export const useFocusFirstInteractiveElement = ({
  isOpen, 
  currentStep = 1,
  delay = 100
}: UseFocusFirstInteractiveElementProps): void => {
  
  useEffect(() => {
    if (!isOpen) return;

    // Use a small delay to ensure the DOM is fully rendered
    const timeoutId = setTimeout(() => {
      // Selectors for interactive elements in order of preference
      const selectors = [
        'input:not([disabled])', 
        'select:not([disabled])', 
        'textarea:not([disabled])',
        'button:not([disabled])',
        '[role="button"]:not([disabled])'
      ].join(', ');

      // Try to find an interactive element within the current step content
      const stepSelector = `[data-step="${currentStep}"]`;
      const stepElement = document.querySelector(stepSelector);
      
      if (stepElement) {
        const firstInteractive = stepElement.querySelector<HTMLElement>(selectors);
        if (firstInteractive) {
          firstInteractive.focus();
          return;
        }
      }

      // Fallback: focus first interactive element anywhere in the dialog
      const dialogSelector = '[role="dialog"]';
      const dialogElement = document.querySelector(dialogSelector);
      
      if (dialogElement) {
        const firstInteractive = dialogElement.querySelector<HTMLElement>(selectors);
        if (firstInteractive) {
          firstInteractive.focus();
        }
      }
    }, delay);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, currentStep, delay]);
};
